import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { MarketingTarotCard } from './MarketingTarotCard';
import { Button } from '@/components/ui/button';
import { getCombinationInterpretation } from '@/data/cardCombinations';
import { 
  RotateCcw, 
  Sparkles,
  ArrowLeft,
  ArrowRight,
  Quote,
  MessageSquare,
  Mail,
  Linkedin,
  Send,
  Loader2,
  CheckCircle,
} from 'lucide-react';

export const ThreeCardSpread = ({ 
  cards, 
  onReset,
  className 
}) => {
  const [flippedCards, setFlippedCards] = useState([false, false, false]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [allRevealed, setAllRevealed] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [combinationInterpretation, setCombinationInterpretation] = useState(null);
  const [insight, setInsight] = useState('');
  const [bestMoveEmail, setBestMoveEmail] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (cards && cards.length === 3) {
      setFlippedCards([false, false, false]);
      setSelectedCard(null);
      setAllRevealed(false);
      setHoveredCard(null);
      setCombinationInterpretation(null);
      
      // Flip cards sequentially with dramatic timing
      const timers = cards.map((_, index) => {
        return setTimeout(() => {
          setFlippedCards(prev => {
            const newState = [...prev];
            newState[index] = true;
            return newState;
          });
        }, 600 + (index * 600));
      });

      // Mark all revealed after last card and get interpretation
      const completeTimer = setTimeout(() => {
        setAllRevealed(true);
        // Get the combination interpretation
        const interpretation = getCombinationInterpretation(cards);
        setCombinationInterpretation(interpretation);
      }, 600 + (cards.length * 600) + 500);

      return () => {
        timers.forEach(t => clearTimeout(t));
        clearTimeout(completeTimer);
      };
    }
  }, [cards]);

  if (!cards || cards.length !== 3) return null;

  // Calculate card positions for hand-of-cards layout
  const getCardStyle = (index) => {
    const isHovered = hoveredCard === index;
    const isSelected = selectedCard === index;
    const offset = index - 1; // -1, 0, 1 for left, center, right
    
    const rotation = offset * 8; // Rotation angle
    const translateX = offset * 70; // Horizontal spread
    const translateY = Math.abs(offset) * 15; // Arc effect
    const zIndex = isHovered || isSelected ? 50 : 10 - Math.abs(offset);
    
    const hoverLift = isHovered ? -25 : 0;
    const hoverScale = isHovered || isSelected ? 1.08 : 1;
    
    return {
      transform: `
        translateX(${translateX}px) 
        translateY(${translateY + hoverLift}px) 
        rotate(${rotation}deg)
        scale(${hoverScale})
      `,
      zIndex,
      transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    };
  };

  return (
    <div className={cn("flex flex-col items-center", className)}>
      {/* Spread Title */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-[#e89b47]"/>
          <h2 className="font-display text-2xl text-[#e8dcc8]">
            Your Best Move Revealed
          </h2>
          <Sparkles className="w-5 h-5 text-[#e89b47]"/>
        </div>
        <p className="text-[#d4c4a8]/60 font-body text-sm">
          Hover over cards to inspect • Click to see details
        </p>
      </div>

      {/* Hand of Cards Layout */}
      <div 
        className="relative flex items-center justify-center mb-6"
        style={{ 
          height: '320px',
          width: '400px',
          perspective: '1200px'
        }}
      >
        {cards.map((card, index) => (
          <div
            key={`${card.id}-${index}`}
            className={cn(
              "absolute cursor-pointer",
              selectedCard === index && "ring-2 ring-[#e89b47] rounded-xl"
            )}
            style={{
              ...getCardStyle(index),
              transformOrigin: 'bottom center',
            }}
            onMouseEnter={() => setHoveredCard(index)}
            onMouseLeave={() => setHoveredCard(null)}
            onClick={() => setSelectedCard(selectedCard === index ? null : index)}
          >
            <MarketingTarotCard
              card={card}
              isFlipped={flippedCards[index]}
              size="default"
              isHoverable={false}
            />
          </div>
        ))}
      </div>

      {/* Combination Interpretation */}
      {allRevealed && combinationInterpretation && (
        <div className={cn(
          "w-full max-w-lg mb-6 animate-fade-in",
          "bg-gradient-to-r from-[#e89b47]/10 via-[#e89b47]/20 to-[#e89b47]/10",
          "border border-[#e89b47]/30 rounded-xl p-5"
        )}>
          <div className="flex items-start gap-3">
            <Quote className="w-6 h-6 text-[#e89b47] flex-shrink-0 mt-1"/>
            <div>
              <p className="font-body text-[#e8dcc8] text-lg leading-relaxed italic">
                "{combinationInterpretation}"
              </p>
              <p className="text-[#e89b47]/70 text-sm mt-2 font-display">
                — {cards.map(c => c.name.replace('The ', '')).join(' + ')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Selected Card Details */}
      {allRevealed && selectedCard !== null && (
        <div className={cn(
          "w-full max-w-md animate-fade-in",
          "bg-[#1e2a4a]/80 backdrop-blur-sm",
          "border-2 border-[#d4c4a8]/30 rounded-xl p-4 mb-4"
        )}>
          <div className="flex items-center justify-between mb-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedCard(Math.max(0, selectedCard - 1))}
              disabled={selectedCard === 0}
              className="text-[#d4c4a8]/60 hover:text-[#d4c4a8]"
            >
              <ArrowLeft className="w-4 h-4 mr-1"/>
              Prev
            </Button>
            <span className="font-display text-[#e89b47] text-sm">
              Card {selectedCard + 1} of 3
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedCard(Math.min(2, selectedCard + 1))}
              disabled={selectedCard === 2}
              className="text-[#d4c4a8]/60 hover:text-[#d4c4a8]"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-1"/>
            </Button>
          </div>
          
          <div className="text-center space-y-2">
            <h3 className="font-display text-xl text-[#e8dcc8]">
              {cards[selectedCard].name}
            </h3>
            
            {cards[selectedCard].isReversed && (
              <span className="inline-block px-2 py-0.5 bg-[#e89b47]/20 border border-[#e89b47]/40 rounded-full text-[#e89b47] text-xs">
                Reversed
              </span>
            )}
            
            {/* Keywords */}
            <div className="flex flex-wrap justify-center gap-2 mt-2">
              {cards[selectedCard].keywords?.map((keyword, idx) => (
                <span 
                  key={idx}
                  className="px-2 py-0.5 bg-[#d4c4a8]/10 border border-[#d4c4a8]/30 rounded-full text-[#d4c4a8]/80 text-xs font-body"
                >
                  {keyword}
                </span>
              ))}
            </div>

            {/* Meaning */}
            <div className="mt-3 pt-3 border-t border-[#d4c4a8]/10">
              <p className="text-[#d4c4a8]/70 font-body text-sm leading-relaxed">
                {cards[selectedCard].isReversed 
                  ? cards[selectedCard].reversed 
                  : cards[selectedCard].meaning}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Insight + Email + LinkedIn Fields */}
      {allRevealed && !isSubmitted && (
        <div className="w-full max-w-md space-y-4 animate-fade-in">
          <div className="space-y-2">
            <label className="flex items-center gap-2 font-display text-[#e8dcc8] text-sm">
              <MessageSquare className="w-4 h-4 text-[#e89b47]" />
              Give us an insight into why this is your best move? You might be featured in our next podcast as a guest!
            </label>
            <textarea
              value={insight}
              onChange={(e) => setInsight(e.target.value)}
              placeholder="Share your thoughts..."
              rows={3}
              className={cn(
                "w-full p-3 rounded-xl border-2 border-[#d4c4a8]/20",
                "bg-[#0a1628]/50 text-[#e8dcc8] placeholder-[#d4c4a8]/30",
                "font-body text-sm resize-none",
                "focus:outline-none focus:border-[#e89b47]/50 focus:ring-1 focus:ring-[#e89b47]/30",
                "transition-all duration-300"
              )}
              data-testid="best-move-insight"
            />
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 font-display text-[#e8dcc8] text-sm">
              <Mail className="w-4 h-4 text-[#e89b47]" />
              Enter your email address
            </label>
            <input
              type="email"
              value={bestMoveEmail}
              onChange={(e) => setBestMoveEmail(e.target.value)}
              placeholder="you@example.com"
              className={cn(
                "w-full p-3 rounded-xl border-2 border-[#d4c4a8]/20",
                "bg-[#0a1628]/50 text-[#e8dcc8] placeholder-[#d4c4a8]/30",
                "font-body text-sm",
                "focus:outline-none focus:border-[#e89b47]/50 focus:ring-1 focus:ring-[#e89b47]/30",
                "transition-all duration-300"
              )}
              data-testid="best-move-email"
            />
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 font-display text-[#e8dcc8] text-sm">
              <Linkedin className="w-4 h-4 text-[#e89b47]" />
              Your LinkedIn Profile
            </label>
            <input
              type="url"
              value={linkedin}
              onChange={(e) => setLinkedin(e.target.value)}
              placeholder="https://linkedin.com/in/yourname"
              className={cn(
                "w-full p-3 rounded-xl border-2 border-[#d4c4a8]/20",
                "bg-[#0a1628]/50 text-[#e8dcc8] placeholder-[#d4c4a8]/30",
                "font-body text-sm",
                "focus:outline-none focus:border-[#e89b47]/50 focus:ring-1 focus:ring-[#e89b47]/30",
                "transition-all duration-300"
              )}
              data-testid="best-move-linkedin"
            />
          </div>
          <Button
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                await fetch(`${backendUrl}/api/bestmove/submit`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    insight: insight.trim(),
                    email: bestMoveEmail.trim(),
                    linkedin: linkedin.trim(),
                    cards: cards.map(c => c.name),
                  }),
                });
                setIsSubmitted(true);
              } catch (err) { console.error('Best move submit failed:', err); }
              setIsSubmitting(false);
            }}
            disabled={isSubmitting}
            className="w-full h-12 bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628] hover:from-[#e89b47]/90 hover:to-[#d4883a]/90 font-display tracking-widest rounded-xl shadow-lg shadow-[#e89b47]/25"
            data-testid="best-move-submit"
          >
            {isSubmitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Send className="w-4 h-4" />
                Submit
              </span>
            )}
          </Button>
        </div>
      )}

      {/* Success message */}
      {allRevealed && isSubmitted && (
        <div className="w-full max-w-md p-6 rounded-2xl bg-[#1e2a4a]/50 border border-green-500/30 text-center animate-fade-in">
          <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
          <p className="font-display text-[#e8dcc8] mb-1">Thank you for sharing!</p>
          <p className="font-body text-[#d4c4a8]/60 text-sm">We'll be in touch if you're selected for the podcast.</p>
        </div>
      )}

    </div>
  );
};

export default ThreeCardSpread;
